// Plain-language wizard for the FIS Prepaid ACC tool, aimed at NON-technical users.
// Each "task" is a friendly Q&A flow; answers are mapped to ACC XML behind the
// scenes using the shared serializer/validators from accRecipes.js.

import { buildBatch, validateField, fieldVisible, PURSE_NAME_RE } from './accRecipes';

export { buildBatch, fieldVisible };

// ---- reusable questions ----------------------------------------------------
const programField = (extra = {}) => ({
  id: 'sp', label: 'Benefit program name', type: 'text', maxLen: 30, required: true,
  placeholder: 'e.g. FSA_PLAN',
  help: 'The internal name FIS uses for this benefit program (the “sub-program”). If you’re not sure, check a recent setup file or ask your FIS contact.',
  ...extra,
});
const purseCodeField = (id = 'pn', extra = {}) => ({
  id, label: 'Purse code', type: 'text', pattern: PURSE_NAME_RE,
  patmsg: 'Use 3 capital letters then 4 numbers — like FSA2026',
  required: true, placeholder: 'e.g. FSA2026',
  help: 'The short code for this pot of money: 3 letters for the type plus a 4-digit year. Example: FSA2026 = Flexible Spending, 2026.',
  ...extra,
});
const yesNo = (a, key) => String(a[key] || '').startsWith('Yes');

// ---- tasks -----------------------------------------------------------------
export const WIZARD_TASKS = [
  {
    id: 'purse-expiry',
    title: 'Change when a purse expires',
    tagline: 'Move a benefit purse’s end date',
    icon: 'CalendarClock',
    intro: 'Use this to change the date a pot of benefit money (a “purse”) stops working — and, if you want, allow a short grace period after that date.',
    steps: [
      { question: 'Which benefit program is this purse in?',
        help: 'Every purse lives inside a benefit program. Tell us which program so we change the right one.',
        fields: [programField()] },
      { question: 'Which purse are you changing?',
        help: 'Purses are identified by a short code. You can change one purse at a time here.',
        fields: [purseCodeField()] },
      { question: 'What should the new expiry date be?',
        help: 'This is the last day cardholders can spend the money in this purse.',
        fields: [{ id: 'xd', label: 'New expiry date', type: 'date', required: true, help: 'Pick the new end date.' }] },
      { question: 'Allow a grace period after the expiry date?',
        help: 'A grace period lets people keep spending for a few extra days after the official end date — useful for purchases already in progress.',
        fields: [
          { id: 'grace', label: 'Grace period', type: 'select', default: 'No — stop exactly on the expiry date',
            options: ['No — stop exactly on the expiry date', 'Yes — allow a short grace period'],
            help: 'Choose “Yes” only if late spending should still be allowed.' },
          { id: 'days', label: 'How many grace days?', type: 'number', min: 1, max: 80, placeholder: 'e.g. 30',
            help: 'Up to 80 days.', required: (a) => yesNo(a, 'grace'), showIf: (a) => yesNo(a, 'grace') },
          { id: 'gdate', label: 'Grace period ends on', type: 'date',
            help: 'The final day spending is allowed.', required: (a) => yesNo(a, 'grace'), showIf: (a) => yesNo(a, 'grace') },
        ] },
    ],
    review: (a) => [
      { label: 'Benefit program', value: a.sp },
      { label: 'Purse', value: a.pn },
      { label: 'New expiry date', value: a.xd },
      { label: 'Grace period', value: yesNo(a, 'grace') ? `Yes — ${a.days} days, until ${a.gdate}` : 'No' },
    ],
    build: (a) => ({ node: 'Purses', rows: [{ node: 'Purse', rows: [
      ['PerformAction', '3'], ['Sub_ProgramName', a.sp], ['PurseName', a.pn], ['ExpDate', a.xd],
      ['ExpExtensionFlag', yesNo(a, 'grace') ? 'POS and Batch' : ''],
      ['ExtensionPeriodinDays', yesNo(a, 'grace') ? a.days : ''],
      ['ExtensionDate', yesNo(a, 'grace') ? a.gdate : ''],
    ] }] }),
  },

  {
    id: 'add-purse',
    title: 'Add a new purse',
    tagline: 'Create a new pot of money on a program',
    icon: 'PlusCircle',
    intro: 'Set up a brand-new benefit purse — its code, its active dates, what it can be spent on, and optional limits.',
    steps: [
      { question: 'Which benefit program will this purse belong to?',
        help: 'The new purse will be added inside this program.',
        fields: [programField()] },
      { question: 'What code and description should the new purse have?',
        help: 'The code identifies the purse internally; the description is a friendly label people will recognize.',
        fields: [
          purseCodeField(),
          { id: 'desc', label: 'Description', type: 'text', maxLen: 30, required: true, placeholder: 'e.g. FSA 2026',
            help: 'A short, human-friendly name for this purse.' },
        ] },
      { question: 'When does this purse start and end?',
        help: 'This sets the window during which money can be spent.',
        fields: [
          { id: 'ed', label: 'Start date', type: 'date', required: true, help: 'First day money can be used.' },
          { id: 'xd', label: 'Expiry date', type: 'date', required: true, help: 'Last day money can be used.' },
        ] },
      { question: 'Where can this purse be spent?',
        help: 'Spending is controlled by a “category group” — a named list of merchant types (like pharmacy or vision) where the money is allowed.',
        fields: [
          { id: 'mcc', label: 'Category group code', type: 'text', pattern: /^\d{5}$/, patmsg: 'A 5-digit group code', required: true, placeholder: 'e.g. 33333',
            help: 'The 5-digit code for the allowed merchant-category group. Ask FIS if you don’t have it.' },
        ] },
      { question: 'Any spending limits? (optional)',
        help: 'Leave these blank for no limit. They cap how much the purse can hold or how much can be added at once.',
        fields: [
          { id: 'maxv', label: 'Maximum balance', type: 'number', placeholder: 'optional', help: 'The most money this purse can ever hold.' },
          { id: 'maxl', label: 'Maximum per load', type: 'number', placeholder: 'optional', help: 'The most that can be added in a single top-up.' },
        ] },
    ],
    review: (a) => [
      { label: 'Benefit program', value: a.sp },
      { label: 'Purse code', value: a.pn },
      { label: 'Description', value: a.desc },
      { label: 'Active dates', value: `${a.ed} → ${a.xd}` },
      { label: 'Spend category group', value: a.mcc },
      { label: 'Maximum balance', value: a.maxv },
      { label: 'Maximum per load', value: a.maxl },
    ],
    build: (a) => ({ node: 'Purses', rows: [{ node: 'Purse', rows: [
      ['PerformAction', '1'], ['Sub_ProgramName', a.sp], ['PurseName', a.pn], ['PurseDescription', a.desc],
      ['EffectiveDate', a.ed], ['ExpDate', a.xd], ['MCCGroup', a.mcc], ['MaxValue', a.maxv], ['MaxLoad', a.maxl],
    ] }] }),
  },

  {
    id: 'card-fee',
    title: 'Change a card fee',
    tagline: 'Update a replacement or reissue fee',
    icon: 'BadgeDollarSign',
    intro: 'Change how much cardholders are charged to replace a lost card or reissue an expired one.',
    steps: [
      { question: 'Which benefit program?',
        help: 'The fee change applies to this program.',
        fields: [programField()] },
      { question: 'Which fee, and what’s the new amount?',
        help: 'Fees must be under $20. Enter 0 to remove the fee completely.',
        fields: [
          { id: 'fee', label: 'Which fee?', type: 'select', default: 'Card Replacement Fee',
            options: ['Card Replacement Fee', 'Card Reissue Fee'],
            help: 'Replacement = lost or stolen card. Reissue = expired or renewed card.' },
          { id: 'amt', label: 'New amount ($)', type: 'number', min: 0, max: 19.99, required: true, placeholder: 'e.g. 7.50',
            help: 'A number between 0 and 19.99.' },
        ] },
    ],
    review: (a) => [
      { label: 'Benefit program', value: a.sp },
      { label: 'Fee', value: a.fee },
      { label: 'New amount', value: a.amt ? `$${a.amt}` : '' },
    ],
    build: (a) => ({ node: 'Configure_Fee', rows: [
      ['PerformAction', '2'], ['Sub_ProgramName', a.sp], ['FeeName', a.fee], ['FeeAmt', a.amt],
    ] }),
  },

  {
    id: 'country',
    title: 'Block or allow a country',
    tagline: 'Control card spending by country',
    icon: 'Globe',
    intro: 'Stop card spending in a specific country, or re-allow a country you previously blocked.',
    steps: [
      { question: 'Which benefit program?',
        help: 'The country rule applies to this program.',
        fields: [programField()] },
      { question: 'What would you like to do?',
        help: 'Choose whether to block or re-allow, and enter the country’s numeric code. Ask FIS for the code if you’re unsure.',
        fields: [
          { id: 'mode', label: 'Action', type: 'select', default: 'Block this country',
            options: ['Block this country', 'Allow this country again'] },
          { id: 'code', label: 'Country code', type: 'text', pattern: /^\d{1,3}$/, patmsg: 'A number from 0 to 999', required: true, placeholder: 'e.g. 249',
            help: 'The numeric country code.' },
          { id: 'resp', label: 'What happens on a blocked purchase?', type: 'select', default: 'Service Restricted',
            options: ['Service Restricted', 'Decline', 'Approval'], showIf: (a) => String(a.mode || '').startsWith('Block'),
            help: 'The message cardholders get when a purchase in this country is stopped.' },
        ] },
    ],
    review: (a) => [
      { label: 'Benefit program', value: a.sp },
      { label: 'Action', value: a.mode },
      { label: 'Country code', value: a.code },
      { label: 'Blocked response', value: String(a.mode || '').startsWith('Block') ? a.resp : '' },
    ],
    build: (a) => ({ node: 'BlockedCountries', rows: [{ node: 'BlockedCountry', rows: [
      ['PerformAction', String(a.mode || '').startsWith('Allow') ? '3' : '1'],
      ['Sub_ProgramName', a.sp], ['CountryCode', a.code],
      ['ResponseDescription', String(a.mode || '').startsWith('Block') ? a.resp : ''],
    ] }] }),
  },

  {
    id: 'mcc-group',
    title: 'Add or remove store categories',
    tagline: 'Edit which merchant types are in a group',
    icon: 'Tags',
    intro: 'Add or remove “merchant category codes” (MCCs) — the codes that classify stores — inside an existing category group.',
    steps: [
      { question: 'Which category group are you editing?',
        help: 'A category group is a named list of merchant types. Enter its name, or its ID number if you have it.',
        fields: [
          { id: 'gname', label: 'Group name', type: 'text', maxLen: 20, placeholder: 'e.g. HEALTHCARE_RX',
            required: (a) => !String(a.gid || '').trim(), help: 'The group’s name. Provide this or the ID below.' },
          { id: 'gid', label: '…or group ID', type: 'text', pattern: /^\d{1,5}$/, patmsg: 'up to 5 digits',
            required: (a) => !String(a.gname || '').trim(), help: 'The group’s numeric ID, if you know it.' },
        ] },
      { question: 'Which categories do you want to add or remove?',
        help: 'Enter the MCC numbers separated by commas. You can fill in one box or both. (MCCs are 4–5 digit codes for merchant types.)',
        fields: [
          { id: 'add', label: 'Categories to add', type: 'text', pattern: /^\d+(\s*,\s*\d+)*$/, patmsg: 'Numbers separated by commas, e.g. 7777,10002',
            required: (a) => !String(a.remove || '').trim(), placeholder: 'e.g. 7777, 10002', help: 'Leave blank if you’re only removing.' },
          { id: 'remove', label: 'Categories to remove', type: 'text', pattern: /^\d+(\s*,\s*\d+)*$/, patmsg: 'Numbers separated by commas',
            required: (a) => !String(a.add || '').trim(), placeholder: 'e.g. 666, 10003', help: 'Leave blank if you’re only adding.' },
        ] },
    ],
    review: (a) => [
      { label: 'Category group', value: a.gname || `ID ${a.gid}` },
      { label: 'Adding', value: a.add },
      { label: 'Removing', value: a.remove },
    ],
    build: (a) => {
      const clean = (s) => String(s || '').replace(/\s+/g, '');
      return { node: 'MCCGroups', rows: [{ node: 'MCCGroup', rows: [
        ['PerformAction', '3'], ['MCCGroupID', a.gid], ['MCCGroupName', a.gname],
        ['MCCAddList', clean(a.add)], ['MCCRemoveList', clean(a.remove)],
      ] }] };
    },
  },
];

// ---- helpers ----------------------------------------------------------------
export function getTask(id) {
  return WIZARD_TASKS.find((t) => t.id === id);
}

export function initialAnswers(task) {
  const a = {};
  for (const s of task.steps) for (const f of s.fields) a[f.id] = f.default ?? '';
  return a;
}

export function validateStepFields(step, answers) {
  const errors = {};
  for (const f of step.fields) {
    if (!fieldVisible(f, answers)) continue;
    const e = validateField(f, answers[f.id], answers);
    if (e) errors[f.id] = e;
  }
  return errors;
}
